import { Block } from "@components/block";
import { Button } from "@components/button";
import { Config, DoubleText, Table } from "@components/table";
import Arrow from "@assets/icons/arrow-right.svg";
import { useNavigate } from "react-router-dom";
import { usePayoutOrders } from "@features/payouts";
import { OrderPayout, OrderPayoutRequest } from "@features/payouts/api";
import { useEffect, useState } from "react";
import { EllipsisText } from "@components/ellipsis-text";
import { formatRubles, formatUsdt } from "@helpers/format";
import Receive from "@assets/icons/receive.svg";
import styles from "./conclusions.module.scss";
import { usePagination } from "@hooks/use-pagination";
import { useUrlFilters } from "@hooks/useUrlFilters";
import { SelectOption } from "@components/select";
import { getBanks } from "@features/bank/api";
import { FilterConfig } from "@components/filter-modal";
import { useWebSocket } from "@request/useWebsocket";
import { WebsocketUpdateType } from "@request/websocket";
import { ApplyPayoutConfirm } from "./confirm";
import { useBoolState } from "@hooks/use-bool-state";

export const ConclusionsPage = () => {
  const config: Config<OrderPayout> = [
    { label: "Способ", source: "method" },
    {
      label: "Номер заявки",
      source: "id",
      renderComponent: (val) => <EllipsisText text={val} copy />,
    },
    {
      label: "Реквизиты",
      source: "requisite",
      renderComponent: (val) => (
        <EllipsisText text={val} copy startLength={100} />
      ),
    },
    { label: "Банк", source: "bank" },
    {
      label: "Сумма ",
      source: "sum",
      renderComponent: (val, item) => {
        return (
          <DoubleText
            text={formatRubles(val)}
            subText={formatUsdt(item.sum_usdt)}
          />
        );
      },
    },
    {
      label: "Доход",
      source: "ern_rub",
      renderComponent: (data) => <div>{formatRubles(data)}</div>,
    },
    {
      label: "Взять в работу",
      source: "commission",
      renderComponent: (_, item) => {
        return (
          <Button
            icon={Receive}
            iconClassName={styles.iconClass}
            onClick={() => {
              setSelectedPayoutId(item.id);
              showConfirm();
            }}
            style={{ float: "right", display: "inline-flex", border: "none" }}
          ></Button>
        );
      },
    },
  ];
  const { setParams } = useUrlFilters();
  const { page, limit, setPage, setLimit } = usePagination(100);
  const navigate = useNavigate();
  const { state, getPayoutOrders, applyOrderForPayout } = usePayoutOrders();
  const [bankOptions, setBankOptions] = useState<SelectOption[]>([]);
  const { lastMessage } = useWebSocket();
  const [isConfirmOpen, showConfirm, hideConfirm] = useBoolState(false);
  const [selectedPayoutId, setSelectedPayoutId] = useState<null | string>(null);

  useEffect(() => {
    if (lastMessage && lastMessage.type == WebsocketUpdateType.PayOut) {
      getPayoutOrders({ page, limit });
    }
  }, [lastMessage, page, limit]);

  useEffect(() => {
    getBanks().then((banks) => {
      const options = banks.map((bank) => ({
        text: bank.name,
        value: bank.id,
      }));

      setBankOptions(options);
    });
  }, []);

  useEffect(() => {
    getPayoutOrders({ limit, page });
  }, [getPayoutOrders, limit, page]);

  const payoutOnConfirm = () => {
    if (selectedPayoutId != null) {
      applyOrderForPayout(selectedPayoutId).then(() => {
        getPayoutOrders({ limit, page });
      });
    }
    onConfirmClose();
  };

  const onConfirmClose = () => {
    setSelectedPayoutId(null);
    hideConfirm();
  };

  const methodOptions: SelectOption[] = [
    { text: "СБП", value: "sbp" },
    { text: "Карта", value: "card" },
    { text: "Счет", value: "account" },
  ];

  const filterConfig: FilterConfig<OrderPayoutRequest> = [
    {
      type: "select",
      options: methodOptions,
      name: "method",
      label: "Метод",
    },
    {
      type: "sum",
      minName: "min_sum",
      maxName: "max_sum",
    },
    {
      type: "select",
      options: bankOptions,
      name: "bank",
      label: "Банк",
    },
  ];

  const onFilterSubmit = (data: OrderPayoutRequest) => {
    setParams(data);
    getPayoutOrders(data);
  };

  return (
    <>
      <Block>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "10px",
          }}
        >
          <h3>Выводы</h3>
          <Button
            icon={Arrow}
            onClick={() => {
              navigate("withdrawals");
            }}
          >
            Мои заявки на вывод
          </Button>
        </div>
        <Table
          config={config}
          data={state.payoutOrders}
          loading={state.loading}
          withFilter
          filterProps={{
            filterConfig,
            // validationRules: validationRules,
            onSubmit: onFilterSubmit,
          }}
          setPage={setPage}
          setLimit={setLimit}
          page={page}
          limit={limit}
          limits={[100, 200, 300]}
          totalCount={state.totalCount}
        />
      </Block>
      {selectedPayoutId != "" && (
        <ApplyPayoutConfirm
          hide={onConfirmClose}
          isOpen={isConfirmOpen}
          onConfirm={payoutOnConfirm}
        />
      )}
    </>
  );
};
